<template>
  <div class="slot-picker-calendar">
    <div class="mode-selector has-margin-bottom-15">
      <b-field>
        <b-radio
          v-if="showTemplateOption"
          v-model="mode"
          native-value="template"
        >
          Use Viewing Slot Template
        </b-radio>
        <b-radio v-model="mode" native-value="manual">
          Pick Times Manually
        </b-radio>
      </b-field>
      <p v-if="!showTemplateOption && viewingSlots.length > 0" class="help has-text-grey">
        <b-icon icon="information-outline" size="is-small"></b-icon>
        Template option only available when start time falls within a prime viewing slot
      </p>
    </div>

    <!-- Template Mode -->
    <div v-if="mode === 'template'" class="template-mode">
      <b-field label="Viewing Slot Template">
        <b-select
          v-model="selectedTemplateOrGroup"
          expanded
          @input="onTemplateChange"
        >
          <option :value="null">Select a template...</option>
          <optgroup v-if="groupedViewingSlots.length > 0" label="Slot Groups">
            <option
              v-for="group in groupedViewingSlots"
              :key="'group-' + group.id"
              :value="{ type: 'group', data: group }"
            >
              {{ group.name }} ({{ group.slots.length }} slots)
            </option>
          </optgroup>
          <optgroup v-if="ungroupedSlots.length > 0" label="Individual Slots">
            <option
              v-for="slot in ungroupedSlots"
              :key="'slot-' + slot.id"
              :value="{ type: 'slot', data: slot }"
            >
              {{ slot.name }}
            </option>
          </optgroup>
        </b-select>
      </b-field>

      <div v-if="selectedTemplate" class="template-info box">
        <p class="has-text-weight-bold">
          <span v-if="isGroupSelection">
            {{ selectedTemplateOrGroup.data.name }}
            <b-tag type="is-info"
              >Group ({{ selectedGroupSlots.length }} slots)</b-tag
            >
          </span>
          <span v-else>{{ selectedTemplate.name }}</span>
        </p>
        <p v-if="isGroupSelection" class="has-text-grey has-margin-bottom-10">
          <strong>Slots in this group:</strong><br />
          <span v-for="(slot, idx) in selectedGroupSlots" :key="slot.id">
            {{ idx + 1 }}. {{ slot.name }}: {{ formatDays(slot.days) }} @
            {{ formatTime(slot.start_time) }}-{{ formatTime(slot.end_time)
            }}<br />
          </span>
        </p>
        <p v-else class="has-text-grey">
          {{ formatDays(selectedTemplate.days) }}<br />
          {{ formatTime(selectedTemplate.start_time) }} -
          {{ formatTime(selectedTemplate.end_time) }}
        </p>

        <p class="has-text-info has-margin-top-10">
          <b-icon icon="information-outline" size="is-small"></b-icon>
          Scheduling {{ episodeCount }} episode{{ episodeCount !== 1 ? 's' : '' }},
          {{ isGroupSelection ? selectedGroupSlots.length : 1 }} slot{{ (isGroupSelection ? selectedGroupSlots.length : 1) !== 1 ? 's' : '' }} each
        </p>

        <div v-if="generatedSlots.length > 0" class="preview has-margin-top-15">
          <p class="has-text-weight-bold">
            Preview ({{ generatedSlots.length }} slots):
          </p>
          <div class="preview-list">
            <div
              v-for="(slot, index) in generatedSlots.slice(0, 10)"
              :key="index"
              class="preview-item"
            >
              ✓ {{ formatSlotPreview(slot) }}
            </div>
            <div v-if="generatedSlots.length > 10" class="has-text-grey">
              ... and {{ generatedSlots.length - 10 }} more
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Manual Picking Mode -->
    <div v-if="mode === 'manual'" class="manual-mode">
      <div class="week-selector has-margin-bottom-15">
        <b-field>
          <b-field label="Week Starting" expanded>
            <p class="control">
              <b-button icon-left="chevron-left" @click="previousWeek">
                Previous Week
              </b-button>
            </p>
            <b-datepicker
              v-model="weekStart"
              placeholder="Select week"
              icon="calendar"
              expanded
              @input="updateWeekGrid"
            />
            <p class="control">
              <b-button icon-right="chevron-right" @click="nextWeek">
                Next Week
              </b-button>
            </p>
          </b-field>
        </b-field>
      </div>

      <div class="timeslot-grid">
        <table class="table is-bordered is-fullwidth is-hoverable">
          <thead>
            <tr>
              <th style="width: 80px">Time</th>
              <th v-for="day in weekDays" :key="day.date">
                {{ day.dayName }}<br />
                <span class="has-text-grey is-size-7">{{ day.dateStr }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="slot in timeSlots" :key="`${slot.hour}-${slot.minute}`">
              <td class="has-text-weight-bold">{{ formatTimeSlot(slot) }}</td>
              <td
                v-for="day in weekDays"
                :key="`${day.date}-${slot.hour}-${slot.minute}`"
                class="timeslot-cell"
                :class="{
                  selected: isSlotSelected(day.date, slot.hour, slot.minute),
                  'has-background-primary-light': isSlotSelected(day.date, slot.hour, slot.minute)
                }"
                @click="toggleSlot(day.date, slot.hour, slot.minute)"
              >
                <div class="slot-checkbox">
                  <b-icon
                    v-if="isSlotSelected(day.date, slot.hour, slot.minute)"
                    icon="check"
                    size="is-small"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="manualSlots.length > 0"
        class="selected-slots has-margin-top-15"
      >
        <div
          class="is-flex is-justify-content-space-between is-align-items-center"
        >
          <p class="has-text-weight-bold">
            Selected Slots ({{ manualSlots.length }}):
          </p>
          <b-button size="is-small" @click="clearAllSlots">Clear All</b-button>
        </div>
        <div class="selected-list has-margin-top-10">
          <b-tag
            v-for="(slot, index) in manualSlots"
            :key="index"
            type="is-primary"
            closable
            class="has-margin-right-5 has-margin-bottom-5"
            @close="removeSlot(index)"
          >
            {{ formatSlotPreview(slot) }}
          </b-tag>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import format from 'date-fns/format'
import addDays from 'date-fns/addDays'
import addWeeks from 'date-fns/addWeeks'
import subWeeks from 'date-fns/subWeeks'
import startOfWeek from 'date-fns/startOfWeek'
import setHours from 'date-fns/setHours'
import setMinutes from 'date-fns/setMinutes'
import isSameDay from 'date-fns/isSameDay'

export default {
  name: 'SlotPickerCalendar',
  props: {
    channelId: {
      type: Number,
      required: true
    },
    duration: {
      type: Number, // Duration in minutes
      default: 60
    },
    initialStartDate: {
      type: Date,
      default: null
    },
    episodeCount: {
      type: Number, // Number of episodes to schedule (for series)
      default: 1
    }
  },
  data() {
    return {
      mode: 'template', // 'template' or 'manual'
      viewingSlots: [],
      selectedTemplate: null,
      selectedTemplateOrGroup: null,
      isGroupSelection: false,
      templateStartDate: this.initialStartDate || new Date(),
      generatedSlots: [],
      weekStart: startOfWeek(this.initialStartDate || new Date(), {
        weekStartsOn: 1
      }), // Monday
      weekDays: [],
      // 30-minute increments: 0:00, 0:30, 1:00, 1:30, ... 23:30
      timeSlots: Array.from({ length: 48 }, (_, i) => ({
        hour: Math.floor(i / 2),
        minute: (i % 2) * 30
      })),
      manualSlots: []
    }
  },
  computed: {
    /**
     * Get the day of week from initialStartDate in database format (1=Mon, 7=Sun)
     */
    startDayOfWeek() {
      if (!this.initialStartDate) return null
      const jsDay = new Date(this.initialStartDate).getDay()
      return jsDay === 0 ? 7 : jsDay // Convert Sunday from 0 to 7
    },
    /**
     * Get all viewing slot groups, filtered to only show groups whose Prime slot
     * (sequence_order=0) includes the selected day of week
     */
    groupedViewingSlots() {
      const groups = {}
      this.viewingSlots
        .filter(slot => slot.slot_group_id)
        .forEach(slot => {
          if (!groups[slot.slot_group_id]) {
            groups[slot.slot_group_id] = {
              id: slot.slot_group_id,
              name: this.getGroupName(slot.slot_group_id),
              slots: []
            }
          }
          groups[slot.slot_group_id].slots.push(slot)
        })

      Object.values(groups).forEach(group => {
        group.slots.sort(
          (a, b) => (a.sequence_order || 0) - (b.sequence_order || 0)
        )
      })

      // Filter groups: only show if the prime slot (sequence_order=0) includes startDayOfWeek
      if (this.startDayOfWeek) {
        return Object.values(groups).filter(group => {
          const primeSlot = group.slots.find(s => (s.sequence_order || 0) === 0)
          if (!primeSlot) return false
          const slotDays = primeSlot.days.split(',').map(Number)
          return slotDays.includes(this.startDayOfWeek)
        })
      }

      return Object.values(groups)
    },
    /**
     * Get ungrouped slots, filtered to only show slots that include the selected day
     */
    ungroupedSlots() {
      const ungrouped = this.viewingSlots.filter(slot => !slot.slot_group_id)

      // Filter by startDayOfWeek if available
      if (this.startDayOfWeek) {
        return ungrouped.filter(slot => {
          const slotDays = slot.days.split(',').map(Number)
          return slotDays.includes(this.startDayOfWeek)
        })
      }

      return ungrouped
    },
    selectedGroupSlots() {
      if (!this.selectedTemplateOrGroup) return []

      if (this.selectedTemplateOrGroup.type === 'group') {
        return this.selectedTemplateOrGroup.data.slots
      }

      return []
    },
    /**
     * Check if the template option should be shown.
     * Only show if the initial start time falls within the first slot
     * (lowest sequence_order) of any viewing slot group or ungrouped slot.
     */
    showTemplateOption() {
      if (!this.initialStartDate || this.viewingSlots.length === 0) {
        return false
      }

      const startTime = new Date(this.initialStartDate)
      const startHour = startTime.getHours()
      const startMinute = startTime.getMinutes()
      // JavaScript getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday
      // Database stores: 1=Monday, ..., 6=Saturday, 7=Sunday
      // Convert JS day to database day format
      const jsDay = startTime.getDay()
      const startDay = jsDay === 0 ? 7 : jsDay // Convert Sunday from 0 to 7

      // Get all "first slots" - either first slot of a group (lowest sequence_order)
      // or ungrouped individual slots
      const firstSlots = []

      // Get first slot from each group (lowest sequence_order)
      this.groupedViewingSlots.forEach(group => {
        if (group.slots && group.slots.length > 0) {
          // Slots are already sorted by sequence_order in the groupedViewingSlots computed
          firstSlots.push(group.slots[0])
        }
      })

      // Add ungrouped slots (they are "first" by definition as they're standalone)
      this.ungroupedSlots.forEach(slot => {
        firstSlots.push(slot)
      })

      // Check if the start time falls within any of these first slots
      for (const slot of firstSlots) {
        // Check if the day matches
        const slotDays = slot.days.split(',').map(Number)
        if (!slotDays.includes(startDay)) {
          continue
        }

        // Parse slot times
        const slotStartTime = new Date(slot.start_time)
        const slotEndTime = new Date(slot.end_time)
        const slotStartHour = slotStartTime.getHours()
        const slotStartMinute = slotStartTime.getMinutes()
        const slotEndHour = slotEndTime.getHours()
        const slotEndMinute = slotEndTime.getMinutes()

        // Convert to minutes since midnight for easier comparison
        const startMinutes = startHour * 60 + startMinute
        const slotStartMinutes = slotStartHour * 60 + slotStartMinute
        const slotEndMinutes = slotEndHour * 60 + slotEndMinute

        // Check if start time is within the slot's time range
        // Handle midnight crossover (e.g., 22:00-02:00)
        if (slotEndMinutes < slotStartMinutes) {
          // Slot crosses midnight
          if (startMinutes >= slotStartMinutes || startMinutes < slotEndMinutes) {
            return true
          }
        } else {
          // Normal slot (doesn't cross midnight)
          if (startMinutes >= slotStartMinutes && startMinutes < slotEndMinutes) {
            return true
          }
        }
      }

      return false
    }
  },
  watch: {
    generatedSlots(newSlots) {
      this.$emit('slots-changed', newSlots)
    },
    manualSlots(newSlots) {
      this.$emit('slots-changed', newSlots)
    },
    episodeCount() {
      // Regenerate slots when episode count changes
      this.regenerateSlots()
    },
    initialStartDate(newDate) {
      // Update template start date when parent changes the primary start time
      if (newDate) {
        this.templateStartDate = newDate
        this.weekStart = startOfWeek(newDate, { weekStartsOn: 1 })
        this.updateWeekGrid()
      }
    },
    showTemplateOption: {
      immediate: true,
      handler(canShow) {
        // If template option is no longer available, switch to manual mode
        if (!canShow && this.mode === 'template') {
          this.mode = 'manual'
        }
      }
    },
    viewingSlots: {
      immediate: true,
      handler() {
        // After slots are loaded, check if we should switch to manual mode
        if (!this.showTemplateOption && this.mode === 'template') {
          this.mode = 'manual'
        }
      }
    }
  },
  async created() {
    await this.loadViewingSlots()
    this.updateWeekGrid()
  },
  methods: {
    async loadViewingSlots() {
      try {
        const ChannelViewingSlotApi = require('@/api/channelViewingSlots')
          .default
        this.viewingSlots = await ChannelViewingSlotApi.getByChannel(
          this.channelId
        )
      } catch (error) {
        console.error('Failed to load viewing slots:', error)
      }
    },
    onTemplateChange() {
      if (!this.selectedTemplateOrGroup) {
        this.selectedTemplate = null
        this.isGroupSelection = false
        this.generatedSlots = []
        return
      }

      if (this.selectedTemplateOrGroup.type === 'group') {
        // Group selection - use first slot for preview info
        this.selectedTemplate = this.selectedTemplateOrGroup.data.slots[0]
        this.isGroupSelection = true
        // Automatically generate from entire group
        if (this.templateStartDate) {
          this.useEntireGroup()
        }
      } else {
        // Single slot selection
        this.selectedTemplate = this.selectedTemplateOrGroup.data
        this.isGroupSelection = false
        if (this.templateStartDate) {
          this.generateFromTemplate()
        }
      }
    },
    regenerateSlots() {
      // Call the correct generation method based on selection type
      if (this.isGroupSelection) {
        this.useEntireGroup()
      } else {
        this.generateFromTemplate()
      }
    },
    generateFromTemplate() {
      if (
        !this.selectedTemplate ||
        !this.templateStartDate ||
        !this.episodeCount
      ) {
        this.generatedSlots = []
        return
      }

      const slots = []
      const templateDays = this.selectedTemplate.days.split(',').map(Number)
      const startTime = new Date(this.selectedTemplate.start_time)
      const endTime = new Date(this.selectedTemplate.end_time)
      const templateHour = startTime.getHours()
      const templateMinute = startTime.getMinutes()
      const templateMinutes = templateHour * 60 + templateMinute
      const slotDuration = endTime - startTime

      // Get the actual start time from initialStartDate to calculate offset
      const actualStartTime = new Date(this.initialStartDate || this.templateStartDate)
      const actualStartHour = actualStartTime.getHours()
      const actualStartMinute = actualStartTime.getMinutes()
      const actualStartMinutes = actualStartHour * 60 + actualStartMinute

      // Calculate time offset (e.g., if template is 18:00 and actual is 19:30, offset is +90 min)
      const timeOffsetMinutes = actualStartMinutes - templateMinutes

      // Apply offset to get the adjusted time
      const adjustedMinutes = templateMinutes + timeOffsetMinutes
      const adjustedHour = Math.floor(((adjustedMinutes % 1440) + 1440) % 1440 / 60)
      const adjustedMinute = ((adjustedMinutes % 1440) + 1440) % 60

      let currentDate = new Date(this.templateStartDate)
      let slotsAdded = 0

      // Generate slots until we reach the target count
      while (slotsAdded < this.episodeCount) {
        // JavaScript getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday
        // Database stores: 1=Monday, ..., 6=Saturday, 7=Sunday
        // Convert JS day to database day format
        const jsDay = currentDate.getDay()
        const dayOfWeek = jsDay === 0 ? 7 : jsDay

        // Check if this day matches template days (1=Monday, ..., 6=Saturday, 7=Sunday)
        if (templateDays.includes(dayOfWeek)) {
          const slotStart = setMinutes(
            setHours(new Date(currentDate), adjustedHour),
            adjustedMinute
          )
          const slotEnd = new Date(slotStart.getTime() + slotDuration)

          slots.push({
            start_time: slotStart,
            end_time: slotEnd
          })
          slotsAdded++
        }

        currentDate = addDays(currentDate, 1)

        // Safety: don't generate more than 365 days out
        if (currentDate > addDays(this.templateStartDate, 365)) {
          break
        }
      }

      this.generatedSlots = slots
    },
    updateWeekGrid() {
      const weekDays = []
      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

      for (let i = 0; i < 7; i++) {
        const date = addDays(this.weekStart, i)
        weekDays.push({
          date: date,
          dayName: dayNames[i],
          dateStr: format(date, 'MMM d')
        })
      }

      this.weekDays = weekDays
    },
    previousWeek() {
      this.weekStart = subWeeks(this.weekStart, 1)
      this.updateWeekGrid()
    },
    nextWeek() {
      this.weekStart = addWeeks(this.weekStart, 1)
      this.updateWeekGrid()
    },
    toggleSlot(date, hour, minute) {
      const slotStart = setMinutes(setHours(new Date(date), hour), minute)
      const slotEnd = new Date(slotStart.getTime() + this.duration * 60000)

      const existingIndex = this.manualSlots.findIndex(
        slot =>
          isSameDay(slot.start_time, slotStart) &&
          slot.start_time.getHours() === hour &&
          slot.start_time.getMinutes() === minute
      )

      if (existingIndex >= 0) {
        // Removing an existing slot - always allow
        this.manualSlots.splice(existingIndex, 1)
      } else {
        // Adding a new slot - validate it's not before the prime/initial start date
        if (this.initialStartDate) {
          const initialStart = new Date(this.initialStartDate)

          if (slotStart < initialStart) {
            // Slot is before the prime/commencement date - show warning
            this.$buefy.toast.open({
              message: 'Additional slots must be scheduled AFTER the prime slot time',
              type: 'is-warning',
              duration: 4000,
              position: 'is-top'
            })
            return
          }
        }

        this.manualSlots.push({
          start_time: slotStart,
          end_time: slotEnd
        })
      }

      // Sort by date and time
      this.manualSlots.sort((a, b) => a.start_time - b.start_time)
    },
    isSlotSelected(date, hour, minute) {
      return this.manualSlots.some(
        slot =>
          isSameDay(slot.start_time, date) &&
          slot.start_time.getHours() === hour &&
          slot.start_time.getMinutes() === minute
      )
    },
    removeSlot(index) {
      this.manualSlots.splice(index, 1)
    },
    clearAllSlots() {
      this.manualSlots = []
    },
    formatDays(daysStr) {
      if (!daysStr) return ''
      const days = daysStr.split(',').map(Number)
      // Database stores: 1=Monday, 2=Tuesday, ..., 6=Saturday, 7=Sunday
      const dayNames = {
        1: 'Mon',
        2: 'Tue',
        3: 'Wed',
        4: 'Thu',
        5: 'Fri',
        6: 'Sat',
        7: 'Sun'
      }
      return days.map(d => dayNames[d] || '?').join(', ')
    },
    formatTime(dateStr) {
      const date = new Date(dateStr)
      return format(date, 'HH:mm')
    },
    formatTimeSlot(slot) {
      return `${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`
    },
    formatSlotPreview(slot) {
      return `${format(slot.start_time, 'EEE MMM d')} @ ${format(
        slot.start_time,
        'HH:mm'
      )}`
    },
    getGroupName(groupId) {
      const slotsInGroup = this.viewingSlots.filter(
        s => s.slot_group_id === groupId
      )
      if (!slotsInGroup.length) return groupId

      // Sort by sequence order to get the first slot
      slotsInGroup.sort(
        (a, b) => (a.sequence_order || 0) - (b.sequence_order || 0)
      )
      const firstSlot = slotsInGroup[0]

      // Try to find a common prefix in all slot names
      if (slotsInGroup.length > 1) {
        const names = slotsInGroup.map(s => s.name)
        let commonPrefix = names[0]

        for (let i = 1; i < names.length; i++) {
          let j = 0
          while (
            j < commonPrefix.length &&
            j < names[i].length &&
            commonPrefix[j].toLowerCase() === names[i][j].toLowerCase()
          ) {
            j++
          }
          commonPrefix = commonPrefix.substring(0, j)
        }

        // Clean up the prefix (remove trailing spaces, numbers, special chars)
        commonPrefix = commonPrefix
          .trim()
          .replace(/[\d\s\-_]+$/, '')
          .trim()

        if (commonPrefix.length > 2) {
          return commonPrefix
        }
      }

      // Fallback: use the first slot name or group ID
      return firstSlot.name || groupId
    },
    useEntireGroup() {
      const groupSlots = this.selectedGroupSlots
      if (
        !groupSlots.length ||
        !this.templateStartDate ||
        !this.episodeCount
      ) {
        return
      }

      const allSlots = []
      let slotsAdded = 0

      // Get the prime template slot (first in group)
      const primeTemplate = groupSlots[0]
      const primeTemplateTime = new Date(primeTemplate.start_time)
      const primeTemplateHour = primeTemplateTime.getHours()
      const primeTemplateMinute = primeTemplateTime.getMinutes()
      const primeTemplateMinutes = primeTemplateHour * 60 + primeTemplateMinute

      // Get the actual start time from initialStartDate
      const actualStartTime = new Date(this.initialStartDate || this.templateStartDate)
      const actualStartHour = actualStartTime.getHours()
      const actualStartMinute = actualStartTime.getMinutes()
      const actualStartMinutes = actualStartHour * 60 + actualStartMinute

      // Calculate the time offset between actual start and template prime slot
      // e.g., if template prime is 18:00 and actual start is 19:30, offset is +90 minutes
      const timeOffsetMinutes = actualStartMinutes - primeTemplateMinutes

      // Pre-calculate offsets for each slot in the group
      // Each slot has a days_after_previous value indicating how many days after
      // the PREVIOUS slot in the sequence it should be scheduled.
      // The scheduling must also respect the allowed weekdays (days field).
      //
      // Example with days_after_previous:
      // - Prime (seq 0, days_after=0): Monday 18:00
      // - Repeat 1 (seq 1, days_after=1): Tuesday 10:00 (1 day after Prime)
      // - Repeat 2 (seq 2, days_after=2): Thursday 10:00 (2 days after Repeat 1)
      //
      // If calculated day falls on non-allowed day (e.g., weekend when only Mon-Fri allowed),
      // advance to next allowed day.
      const slotOffsets = groupSlots.map((template, index) => {
        const templateTime = new Date(template.start_time)
        const templateMinutes = templateTime.getHours() * 60 + templateTime.getMinutes()
        const minuteDiff = templateMinutes - primeTemplateMinutes
        const repeatDays = template.days.split(',').map(Number)

        // Use days_after_previous from database, default to 1 for repeats, 0 for prime
        const daysAfterPrevious = template.days_after_previous !== undefined
          ? template.days_after_previous
          : (index === 0 ? 0 : 1)

        return {
          daysAfterPrevious,
          minuteOffset: minuteDiff,
          templateDays: repeatDays,
          isRepeat: index > 0
        }
      })

      // For each episode/occurrence
      while (slotsAdded < this.episodeCount) {
        let primeSlotDate = null
        let previousSlotDate = null // Track previous slot's date for days_after_previous calculation

        // Process each slot in the group (Prime, Repeat 1, Repeat 2, etc.)
        for (let slotIndex = 0; slotIndex < groupSlots.length; slotIndex++) {
          const template = groupSlots[slotIndex]
          const offset = slotOffsets[slotIndex]
          const endTime = new Date(template.end_time)
          const startTime = new Date(template.start_time)
          const slotDuration = endTime - startTime

          // Calculate the actual time for this slot with offset applied
          const templateTime = new Date(template.start_time)
          const templateMinutes = templateTime.getHours() * 60 + templateTime.getMinutes()
          const adjustedMinutes = templateMinutes + timeOffsetMinutes
          const adjustedHour = Math.floor(((adjustedMinutes % 1440) + 1440) % 1440 / 60)
          const adjustedMinute = ((adjustedMinutes % 1440) + 1440) % 60

          let searchDate

          if (slotIndex === 0) {
            // First slot (Prime) - start from templateStartDate or last week + 7 days
            if (slotsAdded === 0) {
              searchDate = new Date(this.templateStartDate)
            } else {
              searchDate = addDays(primeSlotDate, 7)
            }
          } else {
            // Repeat slots - start from days_after_previous days after the PREVIOUS slot
            // (not after prime, but after the slot that came before in sequence)
            searchDate = addDays(previousSlotDate, offset.daysAfterPrevious)

            // IMPORTANT: For the first episode (slotsAdded === 0), ensure we never schedule
            // BEFORE the user's clicked date (templateStartDate). This prevents the system
            // from going backwards in time when a Repeat slot has days_after_previous = 0
            // and an earlier time than Prime.
            if (slotsAdded === 0) {
              const userStartDate = new Date(this.templateStartDate)
              userStartDate.setHours(0, 0, 0, 0) // Start of day
              const searchStartOfDay = new Date(searchDate)
              searchStartOfDay.setHours(0, 0, 0, 0)

              if (searchStartOfDay < userStartDate) {
                // Repeat slot would fall before user's start date
                // Advance to the user's start date instead
                searchDate = new Date(this.templateStartDate)
              }
            }
          }

          let foundSlot = false
          let daysSearched = 0
          const templateDays = offset.templateDays

          // Search for the next valid day (max 14 days to handle weekend skips)
          while (!foundSlot && daysSearched < 14) {
            // JavaScript getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday
            // Database stores: 1=Monday, ..., 6=Saturday, 7=Sunday
            // Convert JS day to database day format
            const jsDay = searchDate.getDay()
            const dayOfWeek = jsDay === 0 ? 7 : jsDay

            if (templateDays.includes(dayOfWeek)) {
              const slotStart = setMinutes(
                setHours(new Date(searchDate), adjustedHour),
                adjustedMinute
              )
              const slotEnd = new Date(slotStart.getTime() + slotDuration)

              allSlots.push({
                start_time: slotStart,
                end_time: slotEnd
              })

              // Save the prime slot date as the base for next episode
              if (slotIndex === 0) {
                primeSlotDate = new Date(searchDate)
              }

              // Save this slot's date as the previous for the next slot in sequence
              previousSlotDate = new Date(searchDate)

              foundSlot = true
            }

            if (!foundSlot) {
              searchDate = addDays(searchDate, 1)
              daysSearched++
            }
          }
        }

        slotsAdded++

        // Safety check
        if (primeSlotDate && primeSlotDate > addDays(this.templateStartDate, 365)) {
          break
        }
      }

      allSlots.sort((a, b) => a.start_time - b.start_time)
      this.generatedSlots = allSlots
    }
  }
}
</script>

<style scoped>
.slot-picker-calendar {
  max-width: 100%;
}

.template-info {
  background: #f5f5f5;
  padding: 15px;
}

.preview-list {
  max-height: 200px;
  overflow-y: auto;
}

.preview-item {
  padding: 3px 0;
  color: #48c774;
}

.timeslot-grid {
  overflow: auto;
  max-height: 500px;
  border: 2px solid #363636;
  border-radius: 4px;
}

/* Sticky header for day names and dates */
.timeslot-grid table thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

.timeslot-grid table thead th {
  background-color: #363636 !important;
  color: #fff !important;
  border-color: #4a4a4a !important;
  border-bottom: 3px solid #00d1b2 !important;
  font-weight: 600;
}

.timeslot-grid table thead th span {
  color: #b5b5b5 !important;
}

/* Row styling with darker edges */
.timeslot-grid table tbody tr {
  border-bottom: 2px solid #dbdbdb;
}

.timeslot-grid table tbody tr:nth-child(even) {
  background-color: #fafafa;
}

.timeslot-grid table tbody tr:hover {
  background-color: #f0f0f0;
}

/* Time column styling */
.timeslot-grid table tbody td:first-child {
  background-color: #f5f5f5;
  border-right: 2px solid #b5b5b5;
  position: sticky;
  left: 0;
  z-index: 5;
}

.timeslot-cell {
  cursor: pointer;
  text-align: center;
  padding: 8px 4px !important;
  transition: background-color 0.2s;
  user-select: none;
  border-right: 1px solid #e8e8e8 !important;
}

.timeslot-cell:hover {
  background-color: #e8f4fc !important;
}

.timeslot-cell.selected {
  cursor: pointer;
  background-color: #00d1b2 !important;
  color: #fff;
}

.slot-checkbox {
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selected-list {
  display: flex;
  flex-wrap: wrap;
}

.mode-selector {
  border-bottom: 1px solid #dbdbdb;
  padding-bottom: 10px;
}
</style>
